import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Client, Measurement, Order, OrderStatus, SubscriptionPlan, UserAccount } from '../types';
import { MEASUREMENT_FIELDS, ORDER_STATUS_LABELS, ORDER_STATUS_OPTIONS } from '../constants';
import { EditIcon, ChevronDownIcon, TapeMeasureIcon, TrashIcon, PlusIcon, BanknoteIcon, StarIcon } from './icons';
import { exportMeasurementToPDF } from '../utils/exportPDF';
import { generateReceiptPDF } from '../utils/receiptGenerator';
import { OrderForm, OrderFormValues } from './OrderForm';

import { Image } from './Image';

interface ClientDetailsProps {
  client: Client;
  onBack: () => void;
  onDeleteMeasurement: (measurementId: string) => void;
  onDeleteClient: (clientId: string) => void;
  onSetMeasurementToEdit: (measurement: Measurement | null) => void;
  onEditClient: (client: Client) => void;
  onAddMeasurement: () => void;
  orders: Order[];
  ordersEnabled: boolean;
  onSaveOrder: (clientId: string, order: OrderFormValues) => Promise<void> | void;
  onDeleteOrder: (orderId: string) => Promise<void> | void;
  onChangeOrderStatus: (orderId: string, status: OrderStatus) => Promise<void> | void;
  onToggleOrderPaid: (orderId: string, paid: boolean) => Promise<void> | void;
  onUploadClientImage: (clientId: string, file: File) => Promise<void>;
  onRemoveClientImage: (clientId: string, imageUrl: string) => Promise<void>;
  onSetClientProfileImage: (clientId: string, imageUrl: string) => Promise<void> | void;
  pendingUploads?: Record<string, { clientId: string; localUrl: string; status: 'pending' | 'uploading' | 'error'; error?: string }>;
  currentUser?: UserAccount | null;
  authToken?: string | null;
}

const hasValue = (value: unknown): boolean => value !== undefined && value !== null && `${value}`.trim() !== '';

const formatMeasurementValue = (value: unknown): string => {
  if (typeof value === 'number') return `${value} cm`;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const cleaned = value.map((entry) => String(entry).trim()).filter((entry) => entry !== '');
    if (cleaned.length === 0) {
      return '';
    }
    const allNumeric = cleaned.every((entry) => !Number.isNaN(Number(entry.replace(',', '.'))));
    return allNumeric ? `${cleaned.join(' / ')} cm` : cleaned.join(' / ');
  }
  if (value && typeof value === 'object') {
    const v = value as { amount?: number; dueDate?: string; paid?: boolean };
    const parts: string[] = [];
    if (typeof v.amount === 'number') parts.push(`${v.amount.toLocaleString('fr-FR')} FCFA`);
    if (typeof v.dueDate === 'string' && v.dueDate) parts.push(`Echeance: ${v.dueDate}`);
    if (typeof v.paid === 'boolean') parts.push(v.paid ? 'Paye' : 'Non paye');
    return parts.length > 0 ? parts.join(' | ') : '';
  }
  return '';
};

const ORDER_STATUS_BADGE_CLASSES: Record<OrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  ready: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

type GenerationFeature = 'measurement_pdf' | 'receipt_document';
const FREE_MONTHLY_GENERATION_LIMIT = 3;
const STARTER_MONTHLY_GENERATION_LIMIT = 10;

const getUsageMonthKey = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

const buildUsageStorageKey = (userId: string, feature: GenerationFeature): string =>
  `cms-usage:${userId}:${feature}:${getUsageMonthKey()}`;

const MeasurementCard: React.FC<{
  measurement: Measurement;
  client: Client;
  onEdit: (measurement: Measurement) => void;
  onDelete: (measurementId: string) => void;
  userPlan?: SubscriptionPlan;
  orders?: Order[];
  ordersEnabled?: boolean;
  onCreateOrder?: (measurementId: string, outfitType: string) => void;
  onQuickReceipt?: (measurement: Measurement) => void;
  onGenerateReceipt?: (order: Order) => void;
  onExportMeasurementPdf?: (measurement: Measurement) => void;
  isGeneratingPDF?: boolean;
}> = ({ measurement, client, onEdit, onDelete, userPlan, orders = [], ordersEnabled = false, onCreateOrder, onQuickReceipt, onGenerateReceipt, onExportMeasurementPdf, isGeneratingPDF = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const fields = MEASUREMENT_FIELDS[measurement.outfitType];
  const date = measurement.date ? new Date(measurement.date) : null;
  const formattedDate =
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
      : 'Date inconnue';
  const attachments = Array.isArray(measurement.attachments) ? measurement.attachments : [];

  // Trouver les commandes liées à cette mesure
  const linkedOrders = orders.filter(o => o.measurementId === measurement.id && !o.isDeleted);
  const hasActiveOrder = linkedOrders.some(o => o.status !== 'delivered' && o.status !== 'cancelled');
  const pendingOrder = linkedOrders.find(o => o.status !== 'delivered' && o.status !== 'cancelled');

  // Calculer les jours restants pour l'échéance
  const daysRemaining = pendingOrder?.dueDate
    ? Math.ceil((new Date(pendingOrder.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const isUrgent = daysRemaining !== null && daysRemaining <= 3 && daysRemaining >= 0;

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('Etes-vous sur de vouloir supprimer ces mesures ?')) {
      onDelete(measurement.id);
    }
  };

  return (
    <div className={`overflow-hidden t-card ${isUrgent ? 'ring-2 ring-amber-400' : ''}`}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left sm:px-5"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-brand-primary dark:text-brand-secondary">{measurement.outfitType}</p>
            {hasActiveOrder && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isUrgent ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                }`}>
                📋 {ORDER_STATUS_LABELS[pendingOrder!.status]}
                {isUrgent && daysRemaining !== null && (
                  <span className="font-bold"> • {daysRemaining}j</span>
                )}
              </span>
            )}
          </div>
          <p className="text-sm t-text-secondary">{formattedDate}</p>
        </div>
        <ChevronDownIcon
          className={`h-5 w-5 t-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 dark:border-slate-700/50 px-4 py-4 sm:px-5">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            {fields.map((field) => {
              const value = measurement[field.key];
              if (!hasValue(value)) {
                return null;
              }
              return (
                <div key={field.key} className="flex items-center justify-between gap-3">
                  <span className="t-text-secondary">{field.label}</span>
                  <span className="font-semibold t-text-primary">{formatMeasurementValue(value)}</span>
                </div>
              );
            })}

            {/* Mesures spécifiques / personnalisées */}
            {Object.keys(measurement).map((key) => {
              const reservedKeys = ['id', 'outfitType', 'date', 'notes', 'updatedAt', 'isDeleted', 'attachments', 'measurementId'];
              const isStandard = fields.some(f => f.key === key);
              const value = measurement[key];

              if (
                !reservedKeys.includes(key) &&
                !isStandard &&
                hasValue(value) &&
                (typeof value === 'string' || typeof value === 'number' || Array.isArray(value))
              ) {
                // Convertir la clé en label lisible (ex: tourDeMollet -> Tour De Mollet)
                const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
                return (
                  <div key={key} className="flex items-center justify-between gap-3 border-l-2 border-brand-secondary/30 pl-2 bg-brand-secondary/5 dark:bg-brand-secondary/10 rounded-r">
                    <span className="text-brand-secondary font-medium">{label}</span>
                    <span className="font-semibold t-text-primary">{formatMeasurementValue(value)}</span>
                  </div>
                );
              }
              return null;
            })}
          </div>

          {hasValue(measurement.notes) && (
            <div className="mt-4 rounded-lg t-subtle p-3 text-sm t-text-secondary">
              <p className="font-semibold t-text-primary">Notes</p>
              <p className="mt-1 leading-relaxed">{measurement.notes}</p>
            </div>
          )}

          {attachments.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold t-text-primary">Images associees</p>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {attachments.map((url) => (
                  <div
                    key={url}
                    className="relative block overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700"
                  >
                    <Image src={url} alt="illustration mesure" className="h-24 w-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            {/* Bouton Suivi - pour les plans Pro/Premium */}
            {ordersEnabled && onCreateOrder && !hasActiveOrder && (
              <button
                onClick={() => onCreateOrder(measurement.id, measurement.outfitType)}
                className="flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-700 transition hover:bg-indigo-200"
                title="Créer un suivi pour cette mesure"
              >
                <span>📋</span> Suivi
              </button>
            )}

            {/* Bouton PDF Direct - si une commande existe déjà */}
            {linkedOrders.length > 0 && onGenerateReceipt && (userPlan === 'free' || userPlan === 'starter' || userPlan === 'pro' || userPlan === 'premium' || userPlan === 'essentiel') && (
              <button
                onClick={() => onGenerateReceipt(linkedOrders[0])}
                disabled={isGeneratingPDF}
                className="flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-700 transition hover:bg-indigo-200 disabled:opacity-50"
                title="Générer le PDF de la commande liée"
              >
                {isGeneratingPDF ? (
                  <div className="h-3 w-3 animate-spin rounded-full border border-indigo-600 border-t-transparent" />
                ) : (
                  linkedOrders[0].paid ? '📄 Reçu' : '📋 Bon'
                )}
              </button>
            )}

            {/* Bouton Reçu Rapide - uniquement si pas de commande existante */}
            {onQuickReceipt && linkedOrders.length === 0 && (
              <button
                onClick={() => onQuickReceipt(measurement)}
                disabled={isGeneratingPDF}
                className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-200 disabled:opacity-50"
                title="Générer un reçu/bon rapide"
              >
                {isGeneratingPDF ? (
                  <div className="h-3 w-3 animate-spin rounded-full border border-emerald-600 border-t-transparent" />
                ) : (
                  <>
                    <BanknoteIcon className="h-4 w-4" /> <span>Reçu</span>
                  </>
                )}
              </button>
            )}

            {/* Export PDF button - only for premium plans */}
            {onExportMeasurementPdf && (userPlan === 'starter' || userPlan === 'pro' || userPlan === 'premium' || userPlan === 'essentiel') && (
              <button
                onClick={() => onExportMeasurementPdf(measurement)}
                className="rounded-full p-2 text-green-600 transition hover:bg-green-100"
                title="Exporter en PDF"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
            <button
              onClick={handleDelete}
              className="rounded-full p-2 text-red-600 transition hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onEdit(measurement)}
              className="rounded-full p-2 text-brand-secondary transition hover:bg-blue-100 dark:hover:bg-blue-900/30"
            >
              <EditIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ClientDetails: React.FC<ClientDetailsProps & { userPlan?: SubscriptionPlan }> = ({
  client,
  onBack,
  onSetMeasurementToEdit,
  onDeleteMeasurement,
  onDeleteClient,
  onEditClient,
  onAddMeasurement,
  orders,
  ordersEnabled,
  onSaveOrder,
  onDeleteOrder,
  onChangeOrderStatus,
  onToggleOrderPaid,
  onUploadClientImage,
  onRemoveClientImage,
  onSetClientProfileImage,
  pendingUploads = {},
  userPlan,
  currentUser,
  authToken,
}) => {
  const clientImages = client.images ?? [];
  const clientFileInputRef = useRef<HTMLInputElement | null>(null);
  const [clientImageUploading, setClientImageUploading] = useState(false);
  const [clientImageError, setClientImageError] = useState<string | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<Order | null>(null);
  const [orderActionError, setOrderActionError] = useState<string | null>(null);
  const [measurementIdForOrder, setMeasurementIdForOrder] = useState<string | null>(null);
  const [outfitTypeForOrder, setOutfitTypeForOrder] = useState<string | null>(null);
  
  // Quick Receipt State
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const [measurementForQuickOrder, setMeasurementForQuickOrder] = useState<Measurement | null>(null);
  const [quickOrderAmount, setQuickOrderAmount] = useState("");
  const [quickOrderIsPaid, setQuickOrderIsPaid] = useState(true);
  const [quickOrderSubmitting, setQuickOrderSubmitting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [, setUsageRefreshTick] = useState(0);
  const [starterRemainingReceipt, setStarterRemainingReceipt] = useState<number | null>(null);
  const [starterRemainingMeasurementPdf, setStarterRemainingMeasurementPdf] = useState<number | null>(null);

  const effectivePlan = userPlan ?? 'free';
  const getPlanMonthlyLimit = (plan: string): number => {
    if (plan === 'free') {
      return FREE_MONTHLY_GENERATION_LIMIT;
    }
    if (plan === 'starter') {
      return STARTER_MONTHLY_GENERATION_LIMIT;
    }
    return -1;
  };
  const monthlyGenerationLimit = getPlanMonthlyLimit(effectivePlan);
  const hasMonthlyLimit = monthlyGenerationLimit > 0;
  const limitLabel = effectivePlan === 'free' ? 'Free' : effectivePlan === 'starter' ? 'Starter' : null;
  const canUseMeasurementPdf = effectivePlan === 'free' || effectivePlan === 'starter' || effectivePlan === 'essentiel' || effectivePlan === 'pro' || effectivePlan === 'premium';
  const canUseReceiptGeneration = effectivePlan === 'free' || effectivePlan === 'starter' || effectivePlan === 'essentiel' || effectivePlan === 'pro' || effectivePlan === 'premium';
  const usageUserId = currentUser?.userId ?? 'guest';

  const getGenerationUsageCount = (feature: GenerationFeature): number => {
    if (typeof window === 'undefined') {
      return 0;
    }
    try {
      const raw = window.localStorage.getItem(buildUsageStorageKey(usageUserId, feature));
      const parsed = raw ? Number.parseInt(raw, 10) : 0;
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    } catch {
      return 0;
    }
  };

  const incrementGenerationUsage = (feature: GenerationFeature): void => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const key = buildUsageStorageKey(usageUserId, feature);
      const next = getGenerationUsageCount(feature) + 1;
      window.localStorage.setItem(key, String(next));
      setUsageRefreshTick((prev) => prev + 1);
    } catch {
      // ignore quota write failures
    }
  };

  const canGenerateWithMonthlyLimit = (feature: GenerationFeature): boolean => {
    if (monthlyGenerationLimit < 0) {
      return true;
    }
    if (monthlyGenerationLimit === 0) {
      return false;
    }
    return getGenerationUsageCount(feature) < monthlyGenerationLimit;
  };

  const remainingStarterGenerations = (feature: GenerationFeature): number | null => {
    if (!hasMonthlyLimit) {
      return null;
    }
    return Math.max(0, monthlyGenerationLimit - getGenerationUsageCount(feature));
  };

  const openSubscriptionPage = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/abonnement/';
    }
  };

  const showMonthlyLimitReached = (featureLabel: string): void => {
    const limit = monthlyGenerationLimit > 0 ? monthlyGenerationLimit : 0;
    const label = limitLabel ? `Limite ${limitLabel}` : 'Limite mensuelle';
    const shouldUpgrade = window.confirm(
      `${label} atteinte: ${limit} generations mensuelles pour ${featureLabel}. Passez au forfait superieur pour continuer. Aller a la page abonnement ?`
    );
    if (shouldUpgrade) {
      openSubscriptionPage();
    }
  };

  const consumeServerQuota = async (feature: GenerationFeature, action: 'check' | 'consume' = 'consume'): Promise<boolean> => {
    if (!authToken) {
      // Fallback local if no token is available.
      return canGenerateWithMonthlyLimit(feature);
    }

    try {
      const response = await fetch('/fedapay_integration/consume_feature_quota.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ feature, action }),
      });
      const raw = await response.text();
      const data = raw ? JSON.parse(raw) : null;
      if (!response.ok || !data || data.status !== 'ok') {
        throw new Error(data?.message || 'Erreur de verification de quota.');
      }
      const remaining = typeof data.remaining === 'number' ? data.remaining : null;
      if (feature === 'receipt_document') {
        setStarterRemainingReceipt(remaining);
      } else {
        setStarterRemainingMeasurementPdf(remaining);
      }
      return data.allowed === true;
    } catch (error) {
      console.error('Erreur quota serveur:', error);
      return canGenerateWithMonthlyLimit(feature);
    }
  };

  useEffect(() => {
    if (!hasMonthlyLimit || !authToken) {
      return;
    }
    void consumeServerQuota('receipt_document', 'check');
    void consumeServerQuota('measurement_pdf', 'check');
  }, [hasMonthlyLimit, authToken]);

  const sortedOrders = useMemo(() => {
    return orders
      .slice()
      .sort((a, b) => {
        const dueA = a.dueDate || '';
        const dueB = b.dueDate || '';
        if (dueA && dueB && dueA !== dueB) {
          return dueA.localeCompare(dueB);
        }
        const updatedA = a.updatedAt || '';
        const updatedB = b.updatedAt || '';
        return updatedB.localeCompare(updatedA);
      });
  }, [orders]);

  const formatDate = (value?: string | null) => {
    if (!value) {
      return 'Non définie';
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.valueOf())) {
      return parsed.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return value;
  };

  const handleClientUploadClick = () => {
    clientFileInputRef.current?.click();
  };

  const handleClientImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setClientImageError(null);
    setClientImageUploading(true);
    try {
      await onUploadClientImage(client.id, file);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Echec de l'ajout d'image.";
      setClientImageError(message);
    } finally {
      setClientImageUploading(false);
    }
  };

  const handleRemoveClientImage = async (imageUrl: string) => {
    if (!window.confirm('Supprimer cette image ?')) {
      return;
    }
    setClientImageError(null);
    setClientImageUploading(true);
    try {
      await onRemoveClientImage(client.id, imageUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Echec de suppression de l'image.";
      setClientImageError(message);
    } finally {
      setClientImageUploading(false);
    }
  };

  const openOrderForm = (order?: Order | null) => {
    setOrderActionError(null);
    setOrderToEdit(order ?? null);
    setShowOrderForm(true);
  };

  // Ouvrir le formulaire de commande depuis une mesure spécifique
  const openOrderFormForMeasurement = (measurementId: string, outfitType: string) => {
    setOrderActionError(null);
    setOrderToEdit(null);
    setMeasurementIdForOrder(measurementId);
    setOutfitTypeForOrder(outfitType);
    setShowOrderForm(true);
  };

  const closeOrderForm = () => {
    setShowOrderForm(false);
    setOrderToEdit(null);
    setOrderActionError(null);
    setMeasurementIdForOrder(null);
    setOutfitTypeForOrder(null);
  };

  const handleSubmitOrder = async (values: OrderFormValues) => {
    try {
      // Ajouter le measurementId si on crée depuis une mesure
      const orderData = measurementIdForOrder
        ? { ...values, measurementId: measurementIdForOrder }
        : values;
      if (ordersEnabled) {
        await onSaveOrder(client.id, orderData);
      }
      closeOrderForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible d'enregistrer la commande.";
      setOrderActionError(message);
    }
  };

  const handleDeleteOrderClick = async (orderId: string) => {
    if (!window.confirm('Supprimer cette commande ?')) {
      return;
    }
    try {
      await onDeleteOrder(orderId);
      setOrderActionError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Suppression de la commande impossible.";
      setOrderActionError(message);
    }
  };

  const handleStatusChange = async (orderId: string, status: OrderStatus) => {
    try {
      await onChangeOrderStatus(orderId, status);
      setOrderActionError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Modification du statut impossible.';
      setOrderActionError(message);
    }
  };

  const handlePaidToggle = async (order: Order) => {
    try {
      await onToggleOrderPaid(order.id, !order.paid);
      setOrderActionError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Impossible de mettre à jour le paiement.';
      setOrderActionError(message);
    }
  };

  const handleGenerateReceipt = async (order: Order) => {
    if (!currentUser || isGeneratingPDF) return;
    if (!canUseReceiptGeneration) return;
    const allowedByServer = await consumeServerQuota('receipt_document', 'consume');
    if (!allowedByServer) {
      showMonthlyLimitReached('les recus et bons');
      return;
    }
    setIsGeneratingPDF(true);
    try {
      await generateReceiptPDF(order, client, currentUser);
      if (hasMonthlyLimit) {
        incrementGenerationUsage('receipt_document');
      }
    } catch (error) {
      console.error("Erreur PDF:", error);
      alert("Erreur lors de la génération du PDF. Veuillez réessayer.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const openQuickReceipt = (measurement: Measurement) => {
    if (!canUseReceiptGeneration) return;
    setMeasurementForQuickOrder(measurement);
    setQuickOrderAmount("");
    setQuickOrderIsPaid(true);
    setShowQuickOrder(true);
  };

  const handleQuickOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!measurementForQuickOrder || !currentUser) return;

    const amount = Number(quickOrderAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      alert("Veuillez entrer un montant valide.");
      return;
    }

    setQuickOrderSubmitting(true);
    try {
      const allowedByServer = await consumeServerQuota('receipt_document', 'consume');
      if (!allowedByServer) {
        showMonthlyLimitReached('les recus et bons');
        setQuickOrderSubmitting(false);
        return;
      }
      // 1. Créer la commande
      const orderData: OrderFormValues = {
        title: `Commande ${measurementForQuickOrder.outfitType}`,
        status: quickOrderIsPaid ? 'delivered' : 'pending',
        outfitType: measurementForQuickOrder.outfitType as any,
        negotiatedAmount: amount,
        paid: quickOrderIsPaid,
        measurementId: measurementForQuickOrder.id,
        dueDate: null,
        deliveredAt: quickOrderIsPaid ? new Date().toISOString() : null,
        notes: "Reçu rapide généré depuis les mesures."
      };

      await onSaveOrder(client.id, orderData);
      
      // 2. Trouver la commande qu'on vient de créer pour avoir le PDF
      // On peut tricher un peu en créant un objet temporaire pour le PDF car on a toutes les infos
      const tempOrder: Order = {
        id: "temp-" + Date.now(),
        clientId: client.id,
        ...orderData,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setIsGeneratingPDF(true);
      await generateReceiptPDF(tempOrder, client, currentUser);
      if (hasMonthlyLimit) {
        incrementGenerationUsage('receipt_document');
      }
      
      setShowQuickOrder(false);
      setMeasurementForQuickOrder(null);
    } catch (error) {
      console.error("Erreur commande rapide:", error);
      alert("Erreur lors de la création de la commande ou du reçu.");
    } finally {
      setQuickOrderSubmitting(false);
      setIsGeneratingPDF(false);
    }
  };

  const handleExportMeasurementPdf = (measurement: Measurement) => {
    if (!canUseMeasurementPdf) return;
    void (async () => {
      const allowedByServer = await consumeServerQuota('measurement_pdf', 'consume');
      if (!allowedByServer) {
        showMonthlyLimitReached('les PDF de mesures');
        return;
      }
      exportMeasurementToPDF(measurement, client);
      if (hasMonthlyLimit) {
        incrementGenerationUsage('measurement_pdf');
      }
    })();
  };

  const handleDeleteClient = () => {
    if (window.confirm(`Etes-vous sur de vouloir supprimer definitivement ${client.name} et toutes ses mesures ?`)) {
      onDeleteClient(client.id);
    }
  };
  const measurements = Array.isArray(client.measurements) ? client.measurements : [];

  return (
    <div className="space-y-6 p-4 pb-24 md:p-6">
      <div className="t-card p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 t-text-primary">
            <h2 className="text-2xl font-bold">{client.name}</h2>
            <p className="text-sm t-text-secondary">{client.gender}</p>
            <p className="text-sm t-text-secondary">{client.contact || 'Aucun contact'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => onEditClient(client)}
              className="rounded-full p-2 text-brand-secondary transition hover:bg-blue-100 dark:hover:bg-blue-900/30"
              aria-label="Modifier le client"
            >
              <EditIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onBack}
              className="rounded-full border border-brand-light dark:border-slate-700 px-3 py-2 text-sm font-medium t-text-primary transition hover:bg-brand-light/60 md:hidden"
            >
              Retour
            </button>
          </div>
        </div>
        <button
          onClick={handleDeleteClient}
          className="mt-4 text-sm font-medium text-red-600 transition hover:underline"
        >
          Supprimer le client
        </button>
        <div className="mt-6 border-t border-gray-100 dark:border-slate-700/50 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold t-text-primary">Images du client</h3>
              <p className="text-sm t-text-secondary">Ajoutez des photos de reference ou des croquis.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClientUploadClick}
                className="inline-flex items-center gap-2 rounded-md bg-brand-secondary px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-accent disabled:opacity-60"
                disabled={clientImageUploading}
              >
                <PlusIcon className="h-4 w-4" />
                {clientImageUploading ? 'Envoi...' : 'Ajouter une image'}
              </button>
              <input
                ref={clientFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleClientImageChange}
              />
            </div>
          </div>
          {clientImageError && (
            <p className="mt-2 text-sm text-red-600">{clientImageError}</p>
          )}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {clientImages.length > 0 ? (
              clientImages.map((url) => {
                const pending = Object.values(pendingUploads).find((p: any) => p.localUrl === url) as { status: string; localUrl: string } | undefined;
                return (
                  <div key={url} className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 group">
                    <Image src={url} alt={`Illustration de ${client.name}`} className={`h-28 w-full object-cover ${pending ? 'opacity-70' : ''}`} />
                    
                    {pending && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        {pending.status === 'uploading' || pending.status === 'pending' ? (
                          <div className="flex flex-col items-center">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            <span className="mt-1 text-[10px] font-bold text-white shadow-sm">Envoi...</span>
                          </div>
                        ) : pending.status === 'error' ? (
                          <div className="flex flex-col items-center p-1 text-center">
                            <span className="text-lg">⚠️</span>
                            <span className="text-[10px] font-bold text-white bg-red-600 px-1 rounded shadow-sm">Échec</span>
                          </div>
                        ) : null}
                      </div>
                    )}

                    <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => onSetClientProfileImage(client.id, url)}
                        className={`rounded-full p-1.5 shadow transition-all ${
                          client.images?.[0] === url 
                            ? 'bg-amber-500 text-white' 
                            : 'bg-white/90 text-amber-500 hover:bg-white'
                        }`}
                        title={client.images?.[0] === url ? "Image de profil actuelle" : "Definir comme photo de profil"}
                      >
                        <StarIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveClientImage(url)}
                      className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-red-600 shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                      aria-label="Supprimer l'image"
                      disabled={clientImageUploading}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="col-span-full rounded-lg t-subtle p-4 text-center text-sm t-text-secondary">
                Aucune image ajoutee pour ce client.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="t-card p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold t-text-primary">Suivi des commandes</h3>
            <p className="text-sm t-text-secondary">
              Organisez vos commandes : statuts, échéances et paiements.
            </p>
          </div>
          {ordersEnabled && (
            <button
              type="button"
              onClick={() => openOrderForm()}
              className="inline-flex items-center gap-2 rounded-md bg-brand-secondary px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-accent"
            >
              <PlusIcon className="h-4 w-4" />
              Nouvelle commande
            </button>
          )}
        </div>
        {orderActionError && (
          <p className="mt-3 text-sm text-red-600">{orderActionError}</p>
        )}
        {!ordersEnabled ? (
          <div className="mt-4 rounded-lg t-subtle p-4 text-sm t-text-secondary">
            <p>Cette fonctionnalite est disponible en Free (3 commandes max) et en Pro/Premium. Passez au forfait superieur pour commencer a suivre vos commandes.</p>
            <a
              href="/abonnement/"
              className="mt-3 inline-flex items-center justify-center rounded-md bg-brand-accent px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-secondary"
            >
              Passer au forfait superieur
            </a>
          </div>
        ) : sortedOrders.length > 0 ? (
          <div className="mt-4 space-y-4">
            {sortedOrders.map((order) => (
              <div
                key={order.id}
                className="rounded-lg border border-gray-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/30 p-4 shadow-sm"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-base font-semibold t-text-primary">{order.title}</p>
                    <p className="text-sm t-text-secondary">
                      Livraison prévue : {formatDate(order.dueDate)}
                    </p>
                    {order.deliveredAt && (
                      <p className="text-sm t-text-secondary">
                        Livré le : {formatDate(order.deliveredAt)}
                      </p>
                    )}
                    {order.negotiatedAmount != null && (
                      <p className="mt-1 text-sm t-text-primary">
                        Montant négocié :{' '}
                        <span className="font-semibold">
                          {order.negotiatedAmount.toLocaleString('fr-FR')} FCFA
                        </span>
                      </p>
                    )}
                    <p className={`mt-1 text-sm ${order.paid ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {order.paid ? 'Paiement reçu' : 'Paiement en attente'}
                    </p>
                    {order.notes && (
                      <p className="mt-2 whitespace-pre-wrap text-sm t-text-secondary">{order.notes}</p>
                    )}
                  </div>
                  <span
                    className={`inline-flex h-fit items-center rounded-full px-3 py-1 text-xs font-semibold ${ORDER_STATUS_BADGE_CLASSES[order.status]}`}
                  >
                    {ORDER_STATUS_LABELS[order.status] ?? order.status}
                  </span>
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-bold uppercase tracking-wide t-text-muted">
                      Statut
                    </label>
                    <select
                      value={order.status}
                      onChange={(event) => handleStatusChange(order.id, event.target.value as OrderStatus)}
                      className="t-input !py-1 !px-2 !w-max"
                    >
                      {ORDER_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handlePaidToggle(order)}
                      className="rounded-md border border-emerald-500 px-3 py-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400 transition hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                    >
                      {order.paid ? 'Marquer comme non payé' : 'Paiement effectué'}
                    </button>
                    <button
                      type="button"
                      onClick={() => openOrderForm(order)}
                      className="rounded-md border border-brand-secondary px-3 py-1 text-sm font-semibold text-brand-secondary transition hover:bg-brand-secondary/10"
                    >
                      Modifier
                    </button>
                    {canUseReceiptGeneration && (
                      <button
                        type="button"
                        onClick={() => handleGenerateReceipt(order)}
                        disabled={isGeneratingPDF || (hasMonthlyLimit && ((starterRemainingReceipt ?? remainingStarterGenerations('receipt_document') ?? 0) <= 0))}
                        className="rounded-md bg-indigo-600 px-3 py-1 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                        title={
                          hasMonthlyLimit
                            ? `${limitLabel ?? 'Mensuel'}: ${(starterRemainingReceipt ?? remainingStarterGenerations('receipt_document') ?? 0)}/${monthlyGenerationLimit} generations restantes ce mois`
                            : 'Generer recu/bon'
                        }
                      >
                        {isGeneratingPDF ? (
                          <div className="h-3 w-3 animate-spin rounded-full border border-white border-t-transparent" />
                        ) : (
                          order.paid ? '📄 Reçu' : '📋 Bon'
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteOrderClick(order.id)}
                      className="rounded-md border border-red-500 px-3 py-1 text-sm font-semibold text-red-600 dark:text-red-400 transition hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 rounded-lg t-subtle p-4 text-sm t-text-secondary">
            Aucune commande enregistrée pour ce client.
          </p>
        )}
      </div>

      <div className="flex items-center t-text-primary">
        <TapeMeasureIcon className="mr-2 h-6 w-6 text-brand-accent" />
        <h3 className="text-xl font-semibold">Mesures enregistrees</h3>
        {hasMonthlyLimit && (
          <span className="ml-3 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            {limitLabel ?? 'Mensuel'} PDF: {(starterRemainingMeasurementPdf ?? remainingStarterGenerations('measurement_pdf') ?? 0)}/{monthlyGenerationLimit}
          </span>
        )}
      </div>

      {measurements.length > 0 ? (
        <div className="space-y-4">
          {measurements
            .slice()
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((measurement) => (
              <MeasurementCard
                key={measurement.id}
                measurement={measurement}
                client={client}
                onEdit={onSetMeasurementToEdit}
                onDelete={onDeleteMeasurement}
                userPlan={userPlan}
                orders={orders}
                ordersEnabled={ordersEnabled}
                onCreateOrder={openOrderFormForMeasurement}
                onQuickReceipt={canUseReceiptGeneration ? openQuickReceipt : undefined}
                onGenerateReceipt={canUseReceiptGeneration ? handleGenerateReceipt : undefined}
                onExportMeasurementPdf={canUseMeasurementPdf ? handleExportMeasurementPdf : undefined}
                isGeneratingPDF={isGeneratingPDF}
              />
            ))}
        </div>
      ) : (
        <div className="t-card py-10 text-center t-text-secondary">
          <TapeMeasureIcon className="mx-auto h-12 w-12 t-text-muted opacity-30" />
          <p className="mt-3">Aucune mesure enregistree pour ce client.</p>
          <button
            onClick={onAddMeasurement}
            className="mt-4 rounded-md bg-brand-secondary px-4 py-2 text-white transition-colors hover:bg-brand-accent"
          >
            Ajouter des mesures
          </button>
        </div>
      )}
      {showOrderForm && ordersEnabled && (
        <OrderForm
          initialOrder={orderToEdit ?? undefined}
          onClose={closeOrderForm}
          onSave={handleSubmitOrder}
        />
      )}

      {/* Quick Receipt Modal */}
      {showQuickOrder && measurementForQuickOrder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="bg-emerald-600 p-4 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <BanknoteIcon className="h-5 w-5" />
                Reçu Rapide
              </h3>
              <p className="text-xs text-emerald-100 mt-1">
                Générer un document pour : {measurementForQuickOrder.outfitType}
              </p>
            </div>
            
            <form onSubmit={handleQuickOrderSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                  Montant (FCFA)
                </label>
                <input
                  autoFocus
                  type="text"
                  inputMode="numeric"
                  value={quickOrderAmount}
                  onChange={(e) => setQuickOrderAmount(e.target.value)}
                  className="t-input text-lg font-bold"
                  placeholder="0"
                  required
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-xl t-subtle">
                <span className="text-sm font-medium t-text-primary">Paiement reçu ?</span>
                <button
                  type="button"
                  onClick={() => setQuickOrderIsPaid(!quickOrderIsPaid)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    quickOrderIsPaid ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      quickOrderIsPaid ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowQuickOrder(false)}
                  className="flex-1 rounded-xl py-3 text-sm font-bold text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={quickOrderSubmitting || isGeneratingPDF}
                  className="flex-2 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-200 hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-50"
                >
                  {(quickOrderSubmitting || isGeneratingPDF) ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (quickOrderIsPaid ? 'Générer Reçu' : 'Générer Bon')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

